import React, { useEffect, useState } from 'react';

import { Skeleton } from '@mui/material';
import _ from 'lodash';

import ContentCard from '../../../../components/ContentCard';
import ContentGroupsList from '../../../../components/ContentGroupsList';
import DetailsModal, { Fields, Values } from '../../../../components/DetailsModal';
import { useAddQuestionMutation } from '../../../../services/exams';
import { Exam } from '../../../../services/exams/types';
import { useGetTopicGroupsQuery, useGetTopicsQuery } from '../../../../services/topics';
import { Topic } from '../../../../services/topics/types';
import { QuestionType } from '../../../../types/common';
import { formatQuestionLabel } from '../../../../utils/common/exams';
import { TopicChip } from '../../index.styles';
import { getTopicsByQuestion } from '../../utils';

const QUESTION_TYPES = [
  {
    value: 'CALCULATION',
    shortLabel: 'CALC',
    label: 'Calculation',
  },
  {
    value: 'PROOF',
    shortLabel: 'PROOF',
    label: 'Proof',
  },
  {
    value: 'SHORT ANSWER',
    shortLabel: 'SA',
    label: 'Short Answer',
  },
  {
    value: 'EXTENDED RESPONSE',
    shortLabel: 'ER',
    label: 'Extended Response',
  },
  {
    value: 'MULTIPLE CHOICE',
    shortLabel: 'MCQ',
    label: 'Multiple Choice',
  },
  {
    value: 'GRAPHING',
    shortLabel: 'G/D',
    label: 'Graphing / Diagrams',
  },
];

const INITIAL_QUESTION_FORM: Values = {
  label: '',
  type: '',
  maxMark: '',
  topics: [],
};

const getQuestionFormFields = (topics?: Topic[]): Fields => ({
  label: {
    editable: true,
    type: 'text',
  },
  type: {
    editable: true,
    type: 'select',
    typeOptions: QUESTION_TYPES.map(({ label, value }) => ({ label, value })),
  },
  maxMark: {
    editable: true,
    type: 'number',
    label: 'Max Mark',
  },
  topics: {
    editable: true,
    type: 'array',
    typeOptions: (topics ?? []).map((topic) => ({
      value: topic.topicId,
      label: topic.label,
    })),
  },
});

interface QuestionsCardProps {
  exam?: Exam,
  selectedQuestionId?: string,
  onQuestionClick?: (questionId: string) => void
}

const QuestionsCard = ({ exam, selectedQuestionId, onQuestionClick }: QuestionsCardProps) => {
  const { data: topics } = useGetTopicsQuery(
    { subject: exam?.subject || '', search: '' }, { skip: !exam?.subject },
  );
  const { data: topicGroups } = useGetTopicGroupsQuery(
    { subject: exam?.subject || '' }, { skip: !exam?.subject },
  );

  const [addQuestionFormOpen, setAddQuestionFormOpen] = useState<boolean>(false);
  const [questionFormFields, setQuestionFormFields] = useState<Fields>(
    getQuestionFormFields(topics?.data));
  const [questionForm, setQuestionForm] = useState<Values>(INITIAL_QUESTION_FORM);

  const resetQuestionFormFields = () => {
    setQuestionFormFields(getQuestionFormFields(topics?.data));
  };

  // Populate topics options
  useEffect(() => {
    if (topics) resetQuestionFormFields();
  }, [topics]);

  useEffect(() => {
    // Add additional fields if the question is a multiple choice
    if (questionForm.type === QuestionType.MultipleChoice && !questionForm.optionsCount) {
      setQuestionFormFields({
        ...questionFormFields,
        optionsCount: {
          editable: true,
          type: 'number',
        },
        answer: {
          editable: true,
          type: 'number',
        },
      });
      setQuestionForm({
        ...questionForm,
        maxMark: questionForm?.maxMark || 1,
        // Most multiple choice questions have 4 options
        optionsCount: 4,
        answer: '',
      });
    } else if (questionForm.type !== QuestionType.MultipleChoice) {
      // Remove addiontional fields if not a multiple choice
      setQuestionFormFields(_.omit({
        ...questionFormFields,
      }, ['optionsCount', 'answer']));
      if (questionForm?.optionsCount !== undefined) {
        setQuestionForm(_.omit({
          ...questionForm,
        }, ['optionsCount', 'answer']));
      }
    }
  }, [questionForm]);

  const [addQuestion, {
    isSuccess: isAddQuestionSuccess,
    isLoading: isAddQuestionLoading,
    error: addQuestionError,
    reset: resetAddQuestion,
  }] = useAddQuestionMutation();

  const resetModal = async () => {
    await resetAddQuestion();
    resetQuestionFormFields();
    setQuestionForm(INITIAL_QUESTION_FORM);
  };

  // Close modal after question is added
  useEffect(() => {
    if (isAddQuestionSuccess) {
      setAddQuestionFormOpen(false);
      resetModal();
    }
  }, [isAddQuestionSuccess]);

  if (!exam) {
    return (
      <Skeleton variant="rectangular" height={128} />
    );
  }

  return (
    <ContentCard variant="outlined" title="Questions">
      <ContentGroupsList
        columns={[
          {
            field: 'label',
            headerName: '#',
            width: 60,
            renderCell: (params) => formatQuestionLabel(params.value),
          },
          {
            field: 'type',
            headerName: 'Type',
            width: 180,
            renderCell: (params) =>
              `${QUESTION_TYPES.find((x) => x.value === params.value)?.shortLabel
                    || params.value} (${params.row.maxMark} mark${params.row.maxMark > 1 ? 's' : ''})`,
          },
          {
            field: 'topics',
            headerName: 'Topics',
            width: 240,
            renderCell: ({ row }: any) => {
              const topicKeys = getTopicsByQuestion(
                row.questionId,
                exam?.topics || {},
              );
              const firstTopic = (topics?.data || []).find((x) => x.topicId === topicKeys[0]);

              return (
                <>
                  {(firstTopic && (
                    <TopicChip
                      key={topicKeys[0]}
                      label={firstTopic.label}
                      $color={(topicGroups?.data || [])
                        .find((x) => x.groupId === firstTopic.topicGroup)?.color}
                      size="small"
                    />
                  ))}
                  {(topicKeys.length > 1 && (
                    <TopicChip
                      key="more"
                      label={`+${topicKeys.length - 1}`}
                      size="small"
                    />
                  )
                  )}
                </>
              );
            },
          },
        ]}
        data={exam?.questions.map((q) => ({
          ...q,
          id: q.questionId,
        }))}
        loading={!exam}
        actions={{
          create: {
            fn: () => setAddQuestionFormOpen(true),
            label: 'Add Question',
            disabled: exam?.analytics.uniqueAttempts > 0,
          },
        }}
        onRowClick={({ row }) => {
          if (onQuestionClick) {
            onQuestionClick(row?.questionId);
          }
        }}
        selectedRowId={selectedQuestionId}
      />
      <DetailsModal
        open={addQuestionFormOpen}
        handleClose={() => setAddQuestionFormOpen(false)}
        error={addQuestionError}
        title="Add New Question"
        fields={questionFormFields}
        onFieldChange={(key, value) => {
          setQuestionForm({
            ...questionForm,
            [key]: value,
          });
        }}
        values={questionForm}
        buttons={[{
          label: 'Add',
          fn: async (values?: Values) => {
            await addQuestion({
              subject: exam?.subject,
              examId: exam?.examId,
              attributes: values,
            });
          },
          loading: isAddQuestionLoading,
        }]}
      />
    </ContentCard>
  );
};

export default QuestionsCard;
